import React, { createContext, useState, useEffect } from 'react';
import { 
  getAuth, 
  signInWithRedirect, 
  onAuthStateChanged, 
  signOut 
} from 'firebase/auth';
import { doc, setDoc, getDoc, getFirestore } from 'firebase/firestore';
import { provider } from '../firebase';

export const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [userAuth, setUserAuth] = useState(null); // Holds Firebase auth user object
  const [userData, setUserData] = useState(null); // Holds Firestore user document data
  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const auth = getAuth();
    const firestore = getFirestore();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUserAuth(user);
      if (user) {
        const userRef = doc(firestore, 'users', user.uid);

        // Fetch user document from Firestore
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          // Include the userId in the userData
          setUserData({ userId: user.uid, ...userDoc.data() });
        } else {
          setUserData(null);
        }
      } else {
        setUserData(null);
      }
      setLoading(false);
    });

    return () => {
      unsubscribe(); // Clean up the subscription
    };
  }, []);

  const createFirestoreUser = async (userData) => {
    if (userAuth) {
      const userRef = doc(getFirestore(), 'users', userAuth.uid);
      const userDocument = {
        ...userData,
        createdAt: new Date(),
      };
      await setDoc(userRef, userDocument);
      // Include the userId in the userData
      setUserData({ userId: userAuth.uid, ...userDocument });
    }
  };

  const signInWithGoogle = () => {
    setLoading(true);
    const auth = getAuth();
    // Force account selection on every sign-in
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    signInWithRedirect(auth, provider);
  };

  const signOutUser = () => {
    setLoading(true);
    const auth = getAuth();
    signOut(auth).then(() => {
      setUserAuth(null);
      setUserData(null);
      setLoading(false);
    }).catch((error) => {
      console.error('Error during sign-out:', error);
      setLoading(false);
    });
  };

  const contextValue = {
    userAuth,
    userData,
    isLoading,
    signInWithGoogle,
    signOutUser,
    createFirestoreUser
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};
